import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Box } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'

import { tokens } from '@configuration/theme'
import Button from '@base/Button/Button.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

const ErrorFallback = () => {
  const intl = useIntl()
  const handleReload = (): void => {
    window.location.reload()
  }
  return (
    <SplitAuthLayout
      heading={intl.formatMessage({ id: 'errorBoundary.title' })}
      dataTestId='error-fallback'
    >
      <Box sx={{ color: tokens.businessBlack60, fontSize: 14, lineHeight: 1.4 }}>
        <FormattedMessage id='errorBoundary.body' />
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Button variant='primary' size='lg' type='button' onClick={handleReload}>
          {intl.formatMessage({ id: 'errorBoundary.button.reload' })}
        </Button>
        <Button variant='bordered' size='lg' component='a' href='/login'>
          {intl.formatMessage({ id: 'errorBoundary.button.backToLogin' })}
        </Button>
      </Box>
    </SplitAuthLayout>
  )
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught error', error, info.componentStack)
  }

  render(): ReactNode {
    const { hasError } = this.state
    const { children } = this.props
    if (!hasError) {
      return children
    }
    return <ErrorFallback />
  }
}
